"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormSignatureService = void 0;
const path_service_1 = require("./path.service");
const field_kinds_enum_1 = require("../models/fields/field-kinds.enum");
const logic_rules_model_1 = require("../models/fields/logic-rules.model");
const signature_logic_types_enum_1 = require("../models/fields/signature-logic-types.enum");
const user_permission_types_enum_1 = require("../models/users/user-permission-types.enum");
const user_signature_permission_levels_model_1 = require("../models/users/user-signature-permission-levels.model");
const user_types_model_1 = require("../models/users/user-types.model");
class FormSignatureService {
    calculateWorkflowColumn(fields, template) {
        let calculation = fields.reduce((signed, item) => (this.isFieldSigned(item) ? signed + 1 : signed), 1);
        // Cap to maximum number of columns
        if (template && template.columns && calculation > template.columns.length) {
            calculation = template.columns.length;
        }
        return calculation;
    }
    isFieldSigned(field) {
        const { kind, signedOn, signatureUrl } = field;
        return kind === field_kinds_enum_1.FieldKinds.Signature && !!signedOn && !!signatureUrl;
    }
    signForm(form, index, { userId, fullName, signatureUrl, signedOn }, template) {
        const fields = form.items && index < form.items.length ? form.items : [];
        // if we're signing this form, set items to readonly
        if (userId) {
            fields.forEach((field, i) => {
                field.readOnly = i <= index ? true : field.readOnly;
            });
        }
        this.setSignatureOnField(fields[index], { userId, fullName, signatureUrl, signedOn });
        form.isInColumn = this.calculateWorkflowColumn(fields, template);
    }
    resetSignatures(form, columnIdToReset = '') {
        var _a;
        const FIRST_COLUMN = 1;
        const COLUMN_NUMBER_OFFSET = 1;
        const INDEX_NOT_FOUND = -1;
        const columnToResetIndex = (_a = form.columns) === null || _a === void 0 ? void 0 : _a.findIndex((column) => String(column._id) === columnIdToReset);
        const columnToReset = columnToResetIndex === INDEX_NOT_FOUND ? FIRST_COLUMN : columnToResetIndex + COLUMN_NUMBER_OFFSET;
        form.isInColumn = columnToReset;
        if (columnToReset === FIRST_COLUMN) {
            this.resetSignatureFields(form.items);
            return;
        }
        const approvalSignatureFieldIndex = this.getPreviousApprovalSignatureFieldIndex(form, columnToResetIndex);
        const indexAfterApprovalSignatureField = approvalSignatureFieldIndex + 1;
        const fieldsAfterApprovalSignature = form.items.slice(indexAfterApprovalSignatureField);
        this.resetSignatureFields(fieldsAfterApprovalSignature);
    }
    getPreviousApprovalSignatureFieldIndex(form, columnToResetIndex) {
        var _a;
        const previousColumnIndex = columnToResetIndex - 1;
        const approvalSignatureFields = (_a = form.items) === null || _a === void 0 ? void 0 : _a.filter((item) => item.kind === field_kinds_enum_1.FieldKinds.Signature);
        const approvalSignatureField = approvalSignatureFields[previousColumnIndex];
        const approvalSignatureFieldIndex = form.items.findIndex((item) => item._id === approvalSignatureField._id);
        return approvalSignatureFieldIndex;
    }
    resetSignatureFields(fieldsAfterApprovalSignature) {
        fieldsAfterApprovalSignature.forEach((field) => {
            field.readOnly = false;
            if (field.kind === field_kinds_enum_1.FieldKinds.Signature) {
                this.setSignatureOnField(field, {});
            }
            if (field.kind === field_kinds_enum_1.FieldKinds.SignatureArray) {
                field.signatures = [];
            }
            if (field.kind === field_kinds_enum_1.FieldKinds.Table || field.kind === field_kinds_enum_1.FieldKinds.PrefilledTable) {
                this.resetTableSignatures(field);
            }
        });
    }
    setSignatureOnField(field, { userId, fullName, signatureUrl, signedOn }) {
        field.userId = userId;
        field.fullName = fullName;
        field.signatureUrl = signatureUrl;
        field.signedOn = userId ? signedOn || new Date() : signedOn;
    }
    resetTableSignatures(field) {
        const rows = field.rows || [];
        rows.forEach((row) => {
            const columns = row.columns || [];
            columns.forEach((cell) => {
                if (cell.signatures) {
                    cell.signatures = [];
                }
            });
        });
    }
    canPerformFieldAction({ user, teamPath, fieldAction, rules }) {
        const matchingSignatureRule = rules === null || rules === void 0 ? void 0 : rules.find((rule) => rule.action === fieldAction);
        if (!matchingSignatureRule) {
            return true;
        }
        const ruleMatchesUserType = (matchingSignatureRule === null || matchingSignatureRule === void 0 ? void 0 : matchingSignatureRule.type) === signature_logic_types_enum_1.SignatureLogicTypes.User;
        if (ruleMatchesUserType) {
            const userType = this.getUserType(user, teamPath);
            return this.isPermissionSufficient(user_signature_permission_levels_model_1.UserTypePermissionLevels, userType, matchingSignatureRule);
        }
        const ruleMatchesPermissionType = (matchingSignatureRule === null || matchingSignatureRule === void 0 ? void 0 : matchingSignatureRule.type) === signature_logic_types_enum_1.SignatureLogicTypes.Permission;
        if (ruleMatchesPermissionType) {
            const permissionType = this.getPermissionType(user, teamPath);
            return this.isPermissionSufficient(user_signature_permission_levels_model_1.UserPermissionTypeLevels, permissionType, matchingSignatureRule);
        }
        return false;
    }
    getUserType(user, teamPath) {
        if (user.isGlobalAdmin) {
            return user_permission_types_enum_1.UserPermissionTypes.GlobalAdmin;
        }
        const pathService = new path_service_1.PathService();
        const [companyId] = pathService.extractFolderIds(teamPath);
        const isVisitor = user.visitorOf && user.visitorOf.some((visitorId) => visitorId.toString() === companyId);
        return isVisitor ? user_types_model_1.UserTypes.Visitor : user_types_model_1.UserTypes.Contributor;
    }
    getPermissionType(user, path) {
        const pathService = new path_service_1.PathService();
        const [companyId, projectId, teamId] = pathService.extractFolderIds(path);
        const permissionMapping = [
            { type: user_permission_types_enum_1.UserPermissionTypes.GlobalAdmin, condition: user.isGlobalAdmin },
            {
                type: user_permission_types_enum_1.UserPermissionTypes.CompanyController,
                condition: user.companyControllerOf.includes(companyId),
            },
            {
                type: user_permission_types_enum_1.UserPermissionTypes.ProjectController,
                condition: user.projectControllerOf.includes(projectId),
            },
            { type: user_permission_types_enum_1.UserPermissionTypes.TeamController, condition: user.teamControllerOf.includes(teamId) },
            { type: user_permission_types_enum_1.UserPermissionTypes.TeamMember, condition: user.standardUserOf.teams.includes(teamId) },
        ];
        const allowedPermission = permissionMapping.find((item) => item.condition);
        return allowedPermission ? allowedPermission.type : user_permission_types_enum_1.UserPermissionTypes.TeamMember;
    }
    isPermissionSufficient(permissionLevels, userType, signatureRule) {
        const userLevel = permissionLevels[userType];
        return signatureRule.values.some((targetLevel) => {
            const targetPermissionLevel = permissionLevels[targetLevel];
            const logicOperator = signatureRule.operator;
            if (logicOperator === logic_rules_model_1.LogicOperators.Equals) {
                return userLevel === targetPermissionLevel || userLevel === user_signature_permission_levels_model_1.GlobalAdminPermissionLevel;
            }
            if (logicOperator === logic_rules_model_1.LogicOperators.EqualsToOrHigherThan) {
                return userLevel >= targetPermissionLevel;
            }
            return false;
        });
    }
}
exports.FormSignatureService = FormSignatureService;
